// Category — the discovery library page for one of the six categories.
//
// Route: /commons/:categoryId
//
// Renders projects whose `category` field matches the URL id, organized
// into four editorial lifecycle sections (canonical Commons rule):
//   1. 🔥 Almost There  — active gathering ≥ 75% people-committed
//   2. 🌱 Growing       — active gathering < 75% (incl. "Trying Again")
//   3. 🌿 It's Happening — threshold crossed / already happening
//   4. 📖 Past Attempts & Stories — completed / closed-not-met /
//      cancelled — the category's memory and evidence
//
// Drafts are excluded from public category discovery (a project
// explicitly labeled "not yet public" should not appear in the public
// library). Retry chains are rendered as units: the current attempt
// leads, historical attempts follow immediately below in reverse
// chronological order, and the whole chain is placed according to
// the current attempt's lifecycle group.
//
// Home v3 is deliberately independent — Home's category doors read
// from `project.homePreview` and are unaffected by this ordering.

import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCommons } from "../lib/store";
import { CATEGORY_BY_ID } from "../lib/categories";
import {
  effectiveStatus,
  totalPeopleCommitted,
  totalPeopleNeeded,
  isLibraryVisible,
} from "../lib/domain";
import ProjectCard from "../components/ProjectCard";
import ProjectCardCompact from "../components/ProjectCardCompact";

// The precise 75% cutoff for Almost There remains an implementation
// choice (see theme.js#statusLabelDynamic). Reused here as-is so a
// card's rendered label and its shelf placement never disagree.
const ALMOST_THERE_RATIO = 0.75;

// ---- lifecycle group assignment for a single project -------------
// Uses effectiveStatus so a fully-met "gathering" project (dynamically
// threshold-crossed) lands in "It's Happening" alongside genuinely
// threshold-crossed projects. Never creates a new stored status.
function groupForProject(project, pledges) {
  if (project.status === "draft") return "hidden";
  const eff = effectiveStatus(project, pledges);
  if (eff === "completed" || eff === "closed-not-met" || eff === "cancelled") {
    return "past";
  }
  if (eff === "threshold-crossed" || eff === "happening") return "happening";
  // eff === "gathering" (or "draft", already filtered)
  const c = totalPeopleCommitted(project, pledges);
  const n = totalPeopleNeeded(project);
  const ratio = n > 0 ? c / n : 0;
  return ratio >= ALMOST_THERE_RATIO ? "almost" : "growing";
}

// ---- sort key for a project within a gathering group -------------
// Momentum first: highest capped ratio wins; ties broken by absolute
// capped commits, then by original seed-array index for stability.
function gatheringSortKey(project, pledges, seedIndex) {
  const c = totalPeopleCommitted(project, pledges);
  const n = totalPeopleNeeded(project);
  const ratio = n > 0 ? c / n : 0;
  // Return a comparator-friendly tuple. Higher-first fields negated
  // so a single `a-b` sort works.
  return [-ratio, -c, seedIndex];
}

// ---- retry chain resolution --------------------------------------
// Walk nextAttemptId forward from `project` until the tail. If the
// chain has ≥ 2 attempts, return { current, historicals }; otherwise
// null (standalone). Historicals are ordered newest → oldest by
// walking previousAttemptId back from the current attempt.
//
// A "chain of 1" (no prev/next links or a broken link) is treated
// as a standalone project. Historical members that live in a
// different category than the current attempt are still gathered
// here — the category page renders only those whose category
// matches this page (see below).
function resolveChain(project, byId) {
  if (!project.previousAttemptId && !project.nextAttemptId) return null;
  // Walk to the tail (the current/latest attempt).
  let tail = project;
  const guard = new Set([tail.id]); // defensive cycle break
  while (tail.nextAttemptId && byId.has(tail.nextAttemptId)) {
    const next = byId.get(tail.nextAttemptId);
    if (guard.has(next.id)) break;
    guard.add(next.id);
    tail = next;
  }
  // Walk back from tail collecting historicals newest → oldest.
  const historicals = [];
  let cursor = tail.previousAttemptId ? byId.get(tail.previousAttemptId) : null;
  const backGuard = new Set([tail.id]);
  while (cursor && !backGuard.has(cursor.id)) {
    backGuard.add(cursor.id);
    historicals.push(cursor);
    cursor = cursor.previousAttemptId ? byId.get(cursor.previousAttemptId) : null;
  }
  if (historicals.length === 0) return null; // chain of one → standalone
  return { current: tail, historicals };
}

// ---- build the ordered render plan for one category --------------
// A "unit" is either a standalone project or a retry-chain group.
// Each unit is placed and sorted by its "anchor" project — for
// standalones that's the project itself; for chains it's the
// current attempt (tail). Historical attempts follow their current
// attempt in place; they do not fight for their own group slot.
function buildRenderPlan(allProjects, categoryId, pledges) {
  const byId = new Map(allProjects.map((p) => [p.id, p]));
  const seedIndex = new Map(allProjects.map((p, i) => [p.id, i]));
  const catProjects = allProjects.filter((p) => p.category === categoryId);

  const seenChainTails = new Set();
  const units = [];

  for (const p of catProjects) {
    // Library visibility (shared with Home preview logic) — drafts and
    // chains-with-draft-tails are excluded from public discovery.
    if (!isLibraryVisible(p, allProjects)) continue;

    const chain = resolveChain(p, byId);
    if (chain) {
      // Chain anchored on its tail (current attempt). If we already
      // processed this chain via a different member, skip.
      if (seenChainTails.has(chain.current.id)) continue;
      seenChainTails.add(chain.current.id);
      // If the current attempt's category is different from this
      // page's, skip — the chain belongs to that other library.
      // (`isLibraryVisible` above already screens out chains whose
      // tail is a draft.)
      if (chain.current.category !== categoryId) continue;
      const anchor = chain.current;
      units.push({
        kind: "chain",
        anchorId: anchor.id,
        group: groupForProject(anchor, pledges),
        sortKey: gatheringSortKey(anchor, pledges, seedIndex.get(anchor.id)),
        anchorSeedIndex: seedIndex.get(anchor.id),
        current: anchor,
        // Only render historicals that belong to this category so
        // cross-category chains degrade gracefully. Today all
        // chain members share a category.
        historicals: chain.historicals.filter((h) => h.category === categoryId),
      });
    } else {
      units.push({
        kind: "solo",
        anchorId: p.id,
        group: groupForProject(p, pledges),
        sortKey: gatheringSortKey(p, pledges, seedIndex.get(p.id)),
        anchorSeedIndex: seedIndex.get(p.id),
        project: p,
      });
    }
  }

  // Group by lifecycle, then sort within group.
  const groups = { almost: [], growing: [], happening: [], past: [] };
  for (const u of units) {
    if (groups[u.group]) groups[u.group].push(u);
  }
  // Almost There + Growing: momentum-first tuple sort.
  const cmpTuple = (a, b) => {
    for (let i = 0; i < 3; i++) if (a.sortKey[i] !== b.sortKey[i]) return a.sortKey[i] - b.sortKey[i];
    return 0;
  };
  groups.almost.sort(cmpTuple);
  groups.growing.sort(cmpTuple);
  // Happening + Past: seed-array order (deterministic, matches authored intent).
  const cmpSeed = (a, b) => a.anchorSeedIndex - b.anchorSeedIndex;
  groups.happening.sort(cmpSeed);
  groups.past.sort(cmpSeed);

  return groups;
}

// ---- Section header ----------------------------------------------
const SECTION_META = {
  almost:    { emoji: "🔥", label: "Almost There" },
  growing:   { emoji: "🌱", label: "Growing" },
  happening: { emoji: "🌿", label: "It's Happening" },
  past:      { emoji: "📖", label: "Past Attempts & Stories" },
};

export default function Category() {
  const { categoryId } = useParams();
  const nav = useNavigate();
  const { data, user } = useCommons();
  const category = CATEGORY_BY_ID[categoryId];

  if (!category) return <Navigate to="/" replace />;
  if (!data || !user) return <div className="c-load">Loading…</div>;

  const groups = buildRenderPlan(data.projects, category.id, data.pledges);
  const totalRendered =
    groups.almost.length + groups.growing.length +
    groups.happening.length + groups.past.length;

  const openProject = (projectId) => {
    nav(`/project/${projectId}`, { state: { fromCategory: category.id } });
  };

  // Card variant dispatch is unchanged from before this rule change:
  // effective status → compact happening / compact story / full card.
  const renderCard = (p) => {
    const status = effectiveStatus(p, data.pledges);
    if (status === "threshold-crossed" || status === "happening") {
      return (
        <ProjectCardCompact
          key={p.id}
          project={p}
          pledges={data.pledges}
          people={data.people}
          onClick={() => openProject(p.id)}
        />
      );
    }
    if (status === "completed" || status === "closed-not-met" || status === "cancelled") {
      return (
        <ProjectCardCompact
          key={p.id}
          project={p}
          pledges={data.pledges}
          people={data.people}
          onClick={() => openProject(p.id)}
        />
      );
    }
    return (
      <ProjectCard
        key={p.id}
        project={p}
        pledges={data.pledges}
        people={data.people}
        onClick={() => openProject(p.id)}
      />
    );
  };

  const renderUnit = (u) => {
    if (u.kind === "solo") return renderCard(u.project);
    return (
      <div className="c-chain-unit" data-testid={`chain-${u.anchorId}`} key={u.anchorId}>
        {renderCard(u.current)}
        {u.historicals.map((h) => renderCard(h))}
      </div>
    );
  };

  const renderSection = (groupKey) => {
    const items = groups[groupKey];
    if (!items.length) return null;
    const meta = SECTION_META[groupKey];
    return (
      <div className="c-cat-section" data-testid={`cat-section-${groupKey}`} key={groupKey}>
        <div className="c-cat-section-head">
          <span className="c-cat-section-emoji" aria-hidden="true">{meta.emoji}</span>
          <span className="c-cat-section-label">{meta.label}</span>
        </div>
        <div className="c-cat-section-body">
          {items.map(renderUnit)}
        </div>
      </div>
    );
  };

  return (
    <div
      className="c-page c-category-page"
      style={{
        // Comfortable bottom breathing room at the end of the library.
        // The persistent Start-a-Project FAB was retired: creation is
        // invited on Home ("Start with an idea →") and at the natural
        // end of each library via `.c-library-outro` below.
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 40px)",
        "--category-accent": category.accent,
        "--category-surface": category.surface,
        "--category-ink": category.ink,
      }}
      data-testid={`category-page-${category.id}`}
    >
      <div className="c-category-topbar">
        <button
          type="button"
          className="c-icon-btn"
          onClick={() => nav("/", { state: { restoreScroll: true } })}
          data-testid="category-back-btn"
          aria-label="Back to Commons"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="c-category-eyebrow">← Commons</div>
      </div>

      <div className="c-category-hero">
        <div className="c-category-hero-emoji" aria-hidden="true">
          {category.emoji}
        </div>
        <h1 className="c-category-hero-title">{category.name}</h1>
        <div className="c-category-hero-desc">{category.descriptor}</div>
      </div>

      <div className="c-category-body" data-tour="library-projects">
        {totalRendered ? (
          <>
            {renderSection("almost")}
            {renderSection("growing")}
            {renderSection("happening")}
            {renderSection("past")}

            {/* Contextual creation invitation — the final sentence of
                the library, not another card or CTA. Sits after the
                past-attempts shelf so it reads as a quiet closing
                thought, not a promotional module. Replaces the
                retired persistent Start-a-Project FAB on this
                surface. */}
            <div className="c-library-outro" data-testid="library-outro">
              <p className="c-library-outro-lead">Have something else in mind?</p>
              <button
                type="button"
                className="c-library-outro-doorway"
                onClick={() => nav("/create")}
                data-testid="library-outro-start"
              >
                Start a project <span aria-hidden="true">→</span>
              </button>
            </div>
          </>
        ) : (
          <div className="c-category-empty" data-testid="category-empty">
            <div className="c-category-empty-title">
              This category is still just possibility.
            </div>
            <div className="c-category-empty-body">
              Start the first one and see who shows up.
            </div>
            <button
              type="button"
              className="c-btn c-btn-primary"
              onClick={() => nav("/create")}
              data-testid="category-empty-start"
            >
              Start a project
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
